/**
 * Engine Definitions
 *
 * Centralized registry of all supported database engines with their
 * capabilities, display info, and parity gaps.
 *
 * This file is process-agnostic (no React/Electron imports).
 */

import type { EngineDefinition, DatabaseEngineId } from './engineTypes';

/**
 * Microsoft SQL Server engine definition.
 * This is the reference implementation - other engines document parity gaps relative to MSSQL.
 */
export const MSSQL_ENGINE: EngineDefinition = {
  id: 'mssql',
  display: {
    displayName: 'Microsoft SQL Server',
    shortName: 'SQL Server',
    icon: 'mssql',
    defaultPort: 1433,
    connectionStringExample: 'Server=localhost;Database=mydb;User Id=sa;Password=xxx;',
  },
  connectionPicker: {
    label: 'SQL Server', group: 'databases', blurb: 'Host, port, SQL, Windows or Entra login',
    summary: 'On-premises, Azure SQL, Managed Instance', keywords: 'mssql microsoft azure sql tsql t-sql managed instance', order: 1,
  },
  capabilities: {
    queryExecution: {
      supportsMultipleResultSets: true,
      supportsServerSideCancellation: true,
      supportsBatchedResultFetching: true,
      supportsStreamingResults: false,
      supportsCursors: true,
      supportsBatchSeparatorGo: true,
      supportsTransactions: true,
      supportsPreparedStatements: true,
    },
    languageServices: {
      hasLanguageServer: true,
      languageServerType: 'sql-tools-service',
      supportsCompletions: true,
      supportsHover: true,
      supportsSignatureHelp: true,
      supportsDiagnostics: true,
      supportsFormatting: true,
    },
    scripting: {
      supportsCreateTable: true,
      supportsAlterTable: true,
      supportsCreateIndex: true,
      supportsCreateView: true,
      supportsCreateProcedure: true,
      supportsCreateFunction: true,
    },
    connection: {
      supportsWindowsAuth: true,
      supportsSSL: true,
      supportsConnectionPooling: true,
      supportsDatabaseSelection: true,
      supportsSchemas: true,
    },
    tableExplorer: {
      supportsQueryPreview: true,
      supportsInboundForeignKeyExplorer: true,
      supportsEditing: true,
    },
    security: {
      supportsSecurityManagement: true,
      supportsServerLevelPrincipals: true, // Logins, Server Roles
      supportsDatabaseUsers: true,         // Users mapped to Logins
      supportsDeny: true,                  // DENY permissions
    },
    intellisense: {
      structuralParser: 'tree-sitter',
      semanticParser: 'none',
      supportsFkDrivenJoin: true,
      supportsAliasResolution: true,
      supportsCteScope: true,
      supportsPackageProc: false,
      keywordCasingConfigurable: true,
    },
    workspaceFeatures: {
      supportsNotebooks: true,
      supportsSchemaOverview: true,
      supportsQueryEditor: true,
      supportsTableExplorer: true,
    },
    spatial: {
      supportsSpatialTypes: true,   // geometry / geography CLR types
      supportsSpatialFilters: true, // STIntersects / STDistance predicates
    },
  },
  dangerousQueryPatterns: {
    alwaysDangerous: [
      { id: 'mssql.drop-structure', pattern: /\bDROP\s+(TABLE|DATABASE|SCHEMA|INDEX)\b/i, severity: 'high', reasonClass: 'destructive' },
      { id: 'mssql.truncate-table', pattern: /\bTRUNCATE\s+TABLE\b/i, severity: 'high', reasonClass: 'destructive' },
      { id: 'mssql.alter-table-drop', pattern: /\bALTER\s+TABLE\s+.*\bDROP\b/i, severity: 'high', reasonClass: 'destructive' },
      // Routine/code objects — dropping these destroys code, not rows.
      { id: 'mssql.drop-routine', pattern: /\bDROP\s+(VIEW|PROCEDURE|FUNCTION|TRIGGER)\b/i, severity: 'low', reasonClass: 'routine-drop' },
    ],
    dangerousWithoutWhere: [
      { id: 'mssql.delete-no-where', pattern: /\bDELETE\s+FROM\b/i },
      { id: 'mssql.update-no-where', pattern: /\bUPDATE\s+\w+\s+SET\b/i },
    ],
    engineSpecific: [
      // Refined (CK-E correction, WS-E 2026-07-12): only a VALUE-SETTING
      // sp_configure call is flagged — the comma marks a second (value)
      // argument. Bare `EXEC sp_configure` / one-arg reads are safe.
      { id: 'mssql.sp-configure-set', pattern: /\bsp_configure\b[^;,]*,/i, reasonClass: 'system-command' },
      { id: 'mssql.xp-cmdshell', pattern: /\bxp_cmdshell\b/i, reasonClass: 'system-command' },
      { id: 'mssql.dbcc-shrink', pattern: /\bDBCC\s+SHRINK/i, reasonClass: 'system-command' },
      // CK-E rule additions (WS-E 2026-07-12):
      { id: 'mssql.shutdown', pattern: /^\s*SHUTDOWN\b/i, reasonClass: 'system-command' },
      { id: 'mssql.dbcc-checkdb-repair', pattern: /\bDBCC\s+CHECKDB\s*\([^)]*\bREPAIR_ALLOW_DATA_LOSS\b/i, reasonClass: 'destructive' },
      { id: 'mssql.dbcc-freeproccache', pattern: /\bDBCC\s+FREEPROCCACHE\b/i, reasonClass: 'system-command' },
      { id: 'mssql.dbcc-dropcleanbuffers', pattern: /\bDBCC\s+DROPCLEANBUFFERS\b/i, reasonClass: 'system-command' },
      { id: 'mssql.dbcc-checkident-reseed', pattern: /\bDBCC\s+CHECKIDENT\s*\([^)]*\bRESEED\b/i, reasonClass: 'system-command' },
    ],
  },
  connectionErrorPatterns: {
    connectionLost: [
      // MSSQL-specific connection errors
      /failed to connect/i,
      /could not connect/i,
      /connection.*lost/i,
      /connection.*closed/i,
      /connection.*reset/i,
      /connection.*refused/i,
      /connection.*timeout/i,
      /is not active/i,
      /connect first/i,
      /not accessible/i,
      /login failed/i,
      /network.*error/i,
      /socket.*error/i,
      /ECONNREFUSED/i,
      /ECONNRESET/i,
      /ETIMEDOUT/i,
      /EHOSTUNREACH/i,
      /ENETUNREACH/i,
    ],
  },
  parityGaps: [], // MSSQL is the reference implementation
};

/**
 * PostgreSQL engine definition.
 */
export const PGSQL_ENGINE: EngineDefinition = {
  id: 'pgsql',
  display: {
    displayName: 'PostgreSQL',
    shortName: 'PostgreSQL',
    icon: 'pgsql',
    defaultPort: 5432,
    connectionStringExample: 'postgresql://user:pass@localhost:5432/mydb',
  },
  connectionPicker: {
    label: 'PostgreSQL', group: 'databases', blurb: 'Host, port, user and password',
    summary: 'Self-hosted or managed PostgreSQL', keywords: 'postgres pg psql pgsql supabase neon', order: 2,
  },
  capabilities: {
    queryExecution: {
      supportsMultipleResultSets: true, // Implemented via statement splitting - each statement executed separately
      supportsServerSideCancellation: true, // Uses pg_cancel_backend for true server-side cancel
      supportsBatchedResultFetching: true, // Uses cursor-based fetching with FETCH batches
      supportsStreamingResults: false,
      supportsCursors: true,
      supportsBatchSeparatorGo: false,
      supportsTransactions: true,
      supportsPreparedStatements: true,
    },
    languageServices: {
      hasLanguageServer: true,
      languageServerType: 'postgres-lsp',
      supportsCompletions: true,
      supportsHover: true,
      supportsSignatureHelp: true,
      supportsDiagnostics: true,
      supportsFormatting: true,
    },
    scripting: {
      supportsCreateTable: true,
      supportsAlterTable: true,
      supportsCreateIndex: true,
      supportsCreateView: true,
      supportsCreateProcedure: true,
      supportsCreateFunction: true,
    },
    connection: {
      supportsWindowsAuth: false,
      supportsSSL: true,
      supportsConnectionPooling: true,
      supportsDatabaseSelection: true,
      supportsSchemas: true,
    },
    tableExplorer: {
      supportsQueryPreview: true,
      supportsInboundForeignKeyExplorer: true,
      supportsEditing: true,
    },
    security: {
      supportsSecurityManagement: true,
      supportsServerLevelPrincipals: true, // Roles (login roles + group roles)
      supportsDatabaseUsers: false,        // PostgreSQL has no database users - roles are server-level
      supportsDeny: false,                 // PostgreSQL only has GRANT/REVOKE, no DENY
    },
    intellisense: {
      structuralParser: 'tree-sitter',
      semanticParser: 'none',
      supportsFkDrivenJoin: true,
      supportsAliasResolution: true,
      supportsCteScope: true,
      supportsPackageProc: false,
      keywordCasingConfigurable: false,
    },
    workspaceFeatures: {
      supportsNotebooks: true,
      supportsSchemaOverview: true,
      supportsQueryEditor: true,
      supportsTableExplorer: true,
    },
    spatial: {
      supportsSpatialTypes: true,   // PostGIS geometry / geography
      supportsSpatialFilters: true, // ST_Intersects / ST_DWithin predicates
    },
  },
  dangerousQueryPatterns: {
    alwaysDangerous: [
      { id: 'pgsql.drop-structure', pattern: /\bDROP\s+(TABLE|DATABASE|SCHEMA|INDEX)\b/i, severity: 'high', reasonClass: 'destructive' },
      { id: 'pgsql.truncate-table', pattern: /\bTRUNCATE\s+TABLE\b/i, severity: 'high', reasonClass: 'destructive' },
      { id: 'pgsql.alter-table-drop', pattern: /\bALTER\s+TABLE\s+.*\bDROP\b/i, severity: 'high', reasonClass: 'destructive' },
      // Routine/code objects — PROCEDURE added in PG 11.
      { id: 'pgsql.drop-routine', pattern: /\bDROP\s+(VIEW|FUNCTION|PROCEDURE|TRIGGER)\b/i, severity: 'low', reasonClass: 'routine-drop' },
      // Low: SQL/PGQ property graph (PG19) — metadata layer over existing
      // tables (like a view); dropping it removes no underlying data.
      { id: 'pgsql.drop-property-graph', pattern: /\bDROP\s+PROPERTY\s+GRAPH\b/i, severity: 'low', reasonClass: 'routine-drop' },
    ],
    dangerousWithoutWhere: [
      { id: 'pgsql.delete-no-where', pattern: /\bDELETE\s+FROM\b/i },
      // Widened to also catch PG19 temporal updates (`UPDATE t FOR PORTION OF
      // period FROM … TO … SET …`) — the old `\w+` table-name match stopped
      // before the FOR PORTION OF clause and never reached SET, so a no-WHERE
      // temporal UPDATE slipped through unflagged. `[^\s;]+` also now matches
      // qualified names like `public.prices`.
      { id: 'pgsql.update-no-where', pattern: /\bUPDATE\s+[^\s;]+(?:\s+FOR\s+PORTION\s+OF\s+.+?)?\s+SET\b/i },
    ],
    engineSpecific: [
      { id: 'pgsql.drop-owned', pattern: /\bDROP\s+OWNED\s+BY\b/i, reasonClass: 'system-command' },
      { id: 'pgsql.reassign-owned', pattern: /\bREASSIGN\s+OWNED\b/i, reasonClass: 'system-command' },
      { id: 'pgsql.alter-system', pattern: /\bALTER\s+SYSTEM\b/i, reasonClass: 'system-command' },
    ],
  },
  connectionErrorPatterns: {
    connectionLost: [
      // PostgreSQL-specific connection errors
      /connection terminated/i,
      /server closed the connection/i,
      /connection unexpectedly closed/i,
      /connection refused/i,
      /connection.*timeout/i,
      /could not connect/i,
      /no connection to the server/i,
      /the database system is.*shutdown/i,
      /password authentication failed/i,
      /FATAL:/i,
      /ECONNREFUSED/i,
      /ECONNRESET/i,
      /ETIMEDOUT/i,
      /EHOSTUNREACH/i,
      /ENETUNREACH/i,
    ],
  },
  parityGaps: [
    {
      feature: 'streaming-results',
      description: 'pg driver supports cursors but full streaming not implemented yet',
      severity: 'minor',
    },
  ],
};

/**
 * SQLite engine definition.
 */
export const SQLITE_ENGINE: EngineDefinition = {
  id: 'sqlite',
  display: {
    displayName: 'SQLite',
    shortName: 'SQLite',
    icon: 'sqlite',
    defaultPort: 0,
    connectionStringExample: 'file:/path/to/database.sqlite',
  },
  connectionPicker: {
    label: 'SQLite', group: 'databases', blurb: 'An existing database file on this machine',
    summary: 'A database file on this machine', keywords: 'file db local', order: 5,
  },
  capabilities: {
    queryExecution: {
      supportsMultipleResultSets: true,
      supportsServerSideCancellation: false,
      supportsBatchedResultFetching: false,
      supportsStreamingResults: false,
      supportsCursors: false,
      supportsBatchSeparatorGo: false,
      supportsTransactions: true,
      supportsPreparedStatements: false,
    },
    languageServices: {
      hasLanguageServer: false,
      languageServerType: 'none',
      supportsCompletions: true,
      supportsHover: true,
      // Shipped: client-side signature help via `computeFunctionSignatureHelp`
      // (functionSignatureHelp.ts) backed by the static `SQLITE_BUILTIN_ROUTINES`
      // map (sqliteBuiltinRoutines.ts — SQLite has no function-signature
      // catalog, so built-ins are hard-coded from the SQLite docs), registered
      // independently of the LSP path in `SqlMonaco.tsx`. Covered by the
      // non-skipped `sqlite-signature-help.spec.ts`. No production reader
      // consumes this flag yet (only `engineTypes.ts` type decl +
      // `engines.test.ts`) — the first future consumer inherits this as
      // ground truth.
      supportsSignatureHelp: true,
      supportsDiagnostics: false,
      supportsFormatting: false,
    },
    scripting: {
      supportsCreateTable: true,
      supportsAlterTable: false,
      supportsCreateIndex: false,
      supportsCreateView: true,
      supportsCreateProcedure: false,
      supportsCreateFunction: false,
    },
    connection: {
      supportsWindowsAuth: false,
      supportsSSL: false,
      supportsConnectionPooling: false,
      supportsDatabaseSelection: false,
      supportsSchemas: false,
    },
    tableExplorer: {
      supportsQueryPreview: true,
      supportsInboundForeignKeyExplorer: true,
      supportsEditing: true,
    },
    security: {
      supportsSecurityManagement: false,
      supportsServerLevelPrincipals: false,
      supportsDatabaseUsers: false,
      supportsDeny: false,
    },
    intellisense: {
      structuralParser: 'tree-sitter',
      semanticParser: 'none',
      supportsFkDrivenJoin: true,
      supportsAliasResolution: true,
      supportsCteScope: true,
      supportsPackageProc: false,
      keywordCasingConfigurable: false,
    },
    workspaceFeatures: {
      supportsNotebooks: true,
      supportsSchemaOverview: true,
      supportsQueryEditor: true,
      supportsTableExplorer: true,
    },
    spatial: {
      // Viewing works via SpatiaLite / GeoPackage blob decode + BLOB sniff.
      supportsSpatialTypes: true,
      // Filters need mod_spatialite; a per-connection probe upgrades this at runtime (T15).
      supportsSpatialFilters: false,
    },
  },
  dangerousQueryPatterns: {
    alwaysDangerous: [
      { id: 'sqlite.drop-structure', pattern: /\bDROP\s+(TABLE|INDEX)\b/i, severity: 'high', reasonClass: 'destructive' },
      // Kept for compatibility even though SQLite rejects TRUNCATE (CK-E §4).
      { id: 'sqlite.truncate-table', pattern: /\bTRUNCATE\s+TABLE\b/i, severity: 'high', reasonClass: 'destructive' },
      { id: 'sqlite.alter-table-drop', pattern: /\bALTER\s+TABLE\s+.*\bDROP\b/i, severity: 'high', reasonClass: 'destructive' },
      // SQLite supports views and triggers but no procs or user SQL fns.
      { id: 'sqlite.drop-routine', pattern: /\bDROP\s+(VIEW|TRIGGER)\b/i, severity: 'low', reasonClass: 'routine-drop' },
    ],
    dangerousWithoutWhere: [
      { id: 'sqlite.delete-no-where', pattern: /\bDELETE\s+FROM\b/i },
      { id: 'sqlite.update-no-where', pattern: /\bUPDATE\s+\w+\s+SET\b/i },
    ],
    engineSpecific: [],
  },
  connectionErrorPatterns: {
    connectionLost: [
      /SQLITE_CANTOPEN/i,
      /SQLITE_NOTADB/i,
      /SQLITE_IOERR/i,
      /database.*closed/i,
      /not a database/i,
    ],
  },
  parityGaps: [
    {
      feature: 'windows-auth',
      description: 'SQLite is file-based and does not support Windows authentication',
      severity: 'minor',
    },
    {
      feature: 'go-batch-separator',
      description: 'SQLite does not support the T-SQL GO batch separator',
      severity: 'minor',
    },
    {
      feature: 'server-side-cancellation',
      description: 'SQLite runs locally; server-side cancellation is not supported',
      severity: 'minor',
    },
    {
      feature: 'batched-result-fetching',
      description: 'Batched result fetching is not implemented for SQLite',
      severity: 'minor',
    },
    {
      feature: 'prepared-statements',
      description: 'Prepared statements are not implemented for SQLite',
      severity: 'minor',
    },
    {
      feature: 'diagnostics',
      description: 'SQLite does not ship with a language server; diagnostics are not supported',
      severity: 'important',
    },
  ],
};

/**
 * MySQL engine definition.
 * Covers both MySQL and MariaDB (auto-detected via variant after connection).
 * Capabilities start conservative (false) and are flipped to true as features ship.
 */
export const MYSQL_ENGINE: EngineDefinition = {
  id: 'mysql',
  display: {
    displayName: 'MySQL',
    shortName: 'MySQL',
    icon: 'mysql',
    defaultPort: 3306,
    connectionStringExample: 'mysql://user:pass@localhost:3306/mydb',
  },
  connectionPicker: {
    label: 'MySQL', group: 'databases', blurb: 'Host, port, user and password',
    summary: 'Self-hosted or managed MySQL', keywords: 'mariadb', order: 3,
  },
  capabilities: {
    queryExecution: {
      supportsMultipleResultSets: true, // mysql2 multipleStatements: true, normalized by mysqlAdapterUtils
      supportsServerSideCancellation: true, // KILL QUERY <threadId> via admin connection
      supportsBatchedResultFetching: false,
      supportsStreamingResults: false,
      supportsCursors: false,
      supportsBatchSeparatorGo: false,
      supportsTransactions: true, // START TRANSACTION / COMMIT / ROLLBACK
      supportsPreparedStatements: false,
    },
    languageServices: {
      hasLanguageServer: false,
      languageServerType: 'none',
      supportsCompletions: true,
      supportsHover: true,
      // Shipped: client-side signature help via `computeFunctionSignatureHelp`
      // (functionSignatureHelp.ts) + the mysql branch in
      // `routineParameterResolver.ts` (catalog-backed routine metadata),
      // registered independently of the LSP path in `SqlMonaco.tsx`. Covered
      // by the non-skipped `mysql-signature-help.spec.ts` (user-defined
      // function + active-parameter advance). No production reader consumes
      // this flag yet (only `engineTypes.ts` type decl + `engines.test.ts`)
      // — the first future consumer inherits this as ground truth.
      supportsSignatureHelp: true,
      supportsDiagnostics: false,
      supportsFormatting: true, // sql-formatter supports MySQL dialect
    },
    scripting: {
      supportsCreateTable: true,
      supportsAlterTable: true,
      supportsCreateIndex: true,
      supportsCreateView: true,
      supportsCreateProcedure: true,
      supportsCreateFunction: true,
    },
    connection: {
      supportsWindowsAuth: false,
      supportsSSL: true,
      supportsConnectionPooling: true,
      supportsDatabaseSelection: true,
      supportsSchemas: false, // MySQL: database = schema (no separate schema concept)
    },
    tableExplorer: {
      supportsQueryPreview: true,
      supportsInboundForeignKeyExplorer: true,
      supportsEditing: true,
    },
    security: {
      supportsSecurityManagement: true,
      supportsServerLevelPrincipals: true,
      supportsDatabaseUsers: false, // MySQL users are server-level
      supportsDeny: false, // MySQL only has GRANT/REVOKE, no DENY
    },
    intellisense: {
      structuralParser: 'tree-sitter',
      semanticParser: 'none',
      supportsFkDrivenJoin: true,
      supportsAliasResolution: true,
      supportsCteScope: true,
      supportsPackageProc: false,
      keywordCasingConfigurable: false,
    },
    workspaceFeatures: {
      supportsNotebooks: true,
      supportsSchemaOverview: true,
      supportsQueryEditor: true,
      supportsTableExplorer: true,
    },
    spatial: {
      supportsSpatialTypes: true,   // GEOMETRY family (SRID + WKB wire)
      supportsSpatialFilters: true, // MBRIntersects / ST_Distance_Sphere predicates
    },
  },
  dangerousQueryPatterns: {
    alwaysDangerous: [
      { id: 'mysql.drop-structure', pattern: /\bDROP\s+(TABLE|DATABASE|INDEX)\b/i, severity: 'high', reasonClass: 'destructive' },
      { id: 'mysql.truncate-table', pattern: /\bTRUNCATE\s+TABLE\b/i, severity: 'high', reasonClass: 'destructive' },
      { id: 'mysql.alter-table-drop', pattern: /\bALTER\s+TABLE\s+.*\bDROP\b/i, severity: 'high', reasonClass: 'destructive' },
      { id: 'mysql.load-data-infile', pattern: /\bLOAD\s+DATA\s+INFILE\b/i, severity: 'high', reasonClass: 'destructive' },
      { id: 'mysql.reset-replication', pattern: /\bRESET\s+(MASTER|SLAVE|REPLICA)\b/i, severity: 'high', reasonClass: 'destructive' },
      // Routine/code objects.
      { id: 'mysql.drop-routine', pattern: /\bDROP\s+(VIEW|PROCEDURE|FUNCTION|TRIGGER|EVENT)\b/i, severity: 'low', reasonClass: 'routine-drop' },
    ],
    dangerousWithoutWhere: [
      { id: 'mysql.delete-no-where', pattern: /\bDELETE\s+FROM\b/i },
      { id: 'mysql.update-no-where', pattern: /\bUPDATE\s+\w+\s+SET\b/i },
    ],
    engineSpecific: [
      { id: 'mysql.flush-privileges', pattern: /\bFLUSH\s+PRIVILEGES\b/i, reasonClass: 'system-command' },
      { id: 'mysql.set-global', pattern: /\bSET\s+GLOBAL\b/i, reasonClass: 'system-command' },
      { id: 'mysql.purge-binary-logs', pattern: /\bPURGE\s+BINARY\s+LOGS\b/i, reasonClass: 'system-command' },
      // CK-E rule addition (WS-E 2026-07-12): MySQL 5.7.9+ SHUTDOWN command.
      { id: 'mysql.shutdown', pattern: /^\s*SHUTDOWN\b/i, reasonClass: 'system-command' },
    ],
  },
  connectionErrorPatterns: {
    connectionLost: [
      /server has gone away/i,
      /Lost connection to MySQL server/i,
      /ECONNREFUSED/i,
      /ETIMEDOUT/i,
      /ER_ACCESS_DENIED_ERROR/i,
      /Connection lost/i,
      /ECONNRESET/i,
      /EHOSTUNREACH/i,
      /ENETUNREACH/i,
      /connection.*closed/i,
      /connection.*refused/i,
    ],
  },
  parityGaps: [
    {
      feature: 'go-batch-separator',
      description: 'MySQL does not support the T-SQL GO batch separator',
      severity: 'minor',
    },
    {
      feature: 'windows-auth',
      description: 'MySQL does not support Windows authentication; uses password auth plugins',
      severity: 'minor',
    },
    {
      feature: 'streaming-results',
      description: 'Streaming results not implemented for MySQL',
      severity: 'minor',
    },
    {
      feature: 'batched-result-fetching',
      description: 'Batched result fetching not implemented for MySQL',
      severity: 'minor',
    },
    {
      feature: 'prepared-statements',
      description: 'Prepared statements not implemented for MySQL',
      severity: 'minor',
    },
    {
      feature: 'actual-execution-plan',
      description: 'EXPLAIN ANALYZE requires MySQL 8.0.18+ or compatible MariaDB; older versions only support estimated plans',
      severity: 'minor',
    },
  ],
};

/**
 * Oracle Database engine definition.
 * Uses oracledb Thin mode driver (pure JS, no Oracle Client needed).
 * Supports Oracle Database 12c Release 2 (12.2) and later.
 * Capabilities start conservative and are flipped to true as features ship.
 */
export const ORACLE_ENGINE: EngineDefinition = {
  id: 'oracle',
  display: {
    displayName: 'Oracle',
    shortName: 'Oracle',
    icon: 'oracle',
    defaultPort: 1521,
    connectionStringExample: 'localhost:1521/FREEPDB1',
  },
  connectionPicker: {
    label: 'Oracle', group: 'databases', blurb: 'Service name, SID, TNS or wallet',
    summary: 'Service name, SID, TNS alias or descriptor', keywords: 'oci plsql', order: 4,
  },
  capabilities: {
    queryExecution: {
      supportsMultipleResultSets: false, // Not shipped yet
      supportsServerSideCancellation: false, // Not shipped yet
      supportsBatchedResultFetching: false,
      supportsStreamingResults: false,
      supportsCursors: false,
      supportsBatchSeparatorGo: false, // Oracle has no batch separator concept
      supportsTransactions: false, // Not shipped yet
      supportsPreparedStatements: false,
    },
    languageServices: {
      hasLanguageServer: false,
      languageServerType: 'none',
      // Shipped: in-house structural/semantic resolver `oracleResolver.ts`
      // (~2.9k lines), wired as the production completion source via
      // `intellisense/orchestrator/resolverRegistry.ts` (`map.set('oracle',
      // oracleResolver)`). No production reader consumes this flag yet
      // (only `engineTypes.ts` type decl + `engines.test.ts`) — the first
      // future consumer inherits this as ground truth.
      supportsCompletions: true,
      // Shipped: schema-aware hover via `oracleHover.ts` (getOracleHoverInfo),
      // wired into `oracleLanguageServiceProvider.getHoverInfo` (a real
      // implementation, unlike that provider's sig-help stub) and served
      // through the same generic hover-provider registration in
      // `SqlMonaco.tsx` that mysql/sqlite hover uses. No production reader
      // consumes this flag yet.
      supportsHover: true,
      // Shipped: client-side signature help via `computeFunctionSignatureHelp`
      // (functionSignatureHelp.ts) + the oracle branch in
      // `routineParameterResolver.ts` (two/three-level SCHEMA.PKG.PROC
      // resolution), registered independently of the (dormant) LSP path in
      // `SqlMonaco.tsx`. Covered by the non-skipped
      // `oracle-signature-help.spec.ts` (standalone function, multi-param,
      // and PACKAGE.PROC calls). No production reader consumes this flag yet.
      supportsSignatureHelp: true,
      supportsDiagnostics: false,
      supportsFormatting: true, // sql-formatter supports plsql dialect
    },
    scripting: {
      supportsCreateTable: true,
      supportsAlterTable: false,
      supportsCreateIndex: false,
      supportsCreateView: true,
      supportsCreateProcedure: true,
      supportsCreateFunction: true,
    },
    connection: {
      supportsWindowsAuth: false, // Oracle uses password auth; external auth is non-goal v1
      supportsSSL: false, // Not shipped yet (wallet/mTLS)
      supportsConnectionPooling: false, // Not shipped yet
      supportsDatabaseSelection: false, // Oracle: connect to service, not "select database"
      supportsSchemas: true, // Oracle schemas are owned by users (schema = user)
    },
    tableExplorer: {
      supportsQueryPreview: true,
      supportsInboundForeignKeyExplorer: true,
      supportsEditing: true,
    },
    security: {
      supportsSecurityManagement: true,
      supportsServerLevelPrincipals: true,  // Users + Roles are server-level
      supportsDatabaseUsers: false,          // Oracle has no database-level users (schema = user)
      supportsDeny: false,                   // Oracle has no DENY — only GRANT/REVOKE
    },
    intellisense: {
      structuralParser: 'tree-sitter',
      semanticParser: 'none',
      supportsFkDrivenJoin: true,
      supportsAliasResolution: true,
      supportsCteScope: true,
      supportsPackageProc: true,
      keywordCasingConfigurable: false,
    },
    workspaceFeatures: {
      // Oracle notebook sessions pin a dedicated pooled connection via
      // electron/notebook/notebookOracleSession.ts (acquireNotebookSession's
      // 'oracle' case), so cell execution and session-state persistence work
      // the same as the other engines.
      supportsNotebooks: true,
      supportsSchemaOverview: true, // getOracleSchemaOverview exists and is routed
      supportsQueryEditor: true,
      supportsTableExplorer: true,
    },
    spatial: {
      supportsSpatialTypes: true,   // SDO_GEOMETRY (Oracle Spatial)
      supportsSpatialFilters: true, // SDO_GEOM.RELATE / SDO_GEOM.SDO_DISTANCE predicates
    },
  },
  dangerousQueryPatterns: {
    alwaysDangerous: [
      // High: data / structural destruction.
      { id: 'oracle.drop-structure', pattern: /\bDROP\s+(TABLE|DATABASE|INDEX|SEQUENCE|SYNONYM)\b/i, severity: 'high', reasonClass: 'destructive' },
      { id: 'oracle.truncate-table', pattern: /\bTRUNCATE\s+TABLE\b/i, severity: 'high', reasonClass: 'destructive' },
      { id: 'oracle.drop-tablespace', pattern: /\bDROP\s+TABLESPACE\b/i, severity: 'high', reasonClass: 'destructive' },
      // Reclassified (CK-E, WS-E 2026-07-12): server-level commands, not
      // data-destructive DDL. Severity stays high.
      { id: 'oracle.shutdown', pattern: /\bSHUTDOWN\b/i, severity: 'high', reasonClass: 'system-command' },
      { id: 'oracle.kill-session', pattern: /\bALTER\s+SYSTEM\s+KILL\s+SESSION\b/i, severity: 'high', reasonClass: 'system-command' },
      // Low: routine/code objects. PACKAGE and TYPE define PL/SQL code.
      { id: 'oracle.drop-routine', pattern: /\bDROP\s+(VIEW|PROCEDURE|FUNCTION|TRIGGER|PACKAGE|TYPE)\b/i, severity: 'low', reasonClass: 'routine-drop' },
      // Low: SQL/PGQ property graph — metadata layer over existing tables (like
      // a view); dropping it removes no underlying data.
      { id: 'oracle.drop-property-graph', pattern: /\bDROP\s+PROPERTY\s+GRAPH\b/i, severity: 'low', reasonClass: 'routine-drop' },
    ],
    dangerousWithoutWhere: [
      { id: 'oracle.delete-no-where', pattern: /\bDELETE\s+FROM\b/i },
      { id: 'oracle.update-no-where', pattern: /\bUPDATE\s+\w+\s+SET\b/i },
    ],
    engineSpecific: [
      { id: 'oracle.alter-system', pattern: /\bALTER\s+SYSTEM\b/i, reasonClass: 'system-command' },
      // `ALTER SESSION SET` removed (CK-E policy correction, WS-E 2026-07-12):
      // session-local, not server-level — no confirmation needed.
      // Widened (CK-E rule addition): the whole PURGE family permanently
      // discards recycle-bin contents, not just PURGE RECYCLEBIN.
      { id: 'oracle.purge', pattern: /\bPURGE\s+(RECYCLEBIN|DBA_RECYCLEBIN|TABLE|INDEX|TABLESPACE)\b/i, reasonClass: 'system-command' },
      { id: 'oracle.flashback-table', pattern: /\bFLASHBACK\s+TABLE\b/i, reasonClass: 'system-command' },
    ],
  },
  connectionErrorPatterns: {
    connectionLost: [
      /ORA-03113/i, // end-of-file on communication channel
      /ORA-03114/i, // not connected to ORACLE
      /ORA-12170/i, // connect timeout
      /ORA-12541/i, // no listener
      /ORA-01017/i, // invalid username/password
      /ORA-12514/i, // service not found
      /ECONNREFUSED/i,
      /ETIMEDOUT/i,
    ],
  },
  parityGaps: [
    {
      feature: 'go-batch-separator',
      description: 'Oracle has no batch separator concept; uses ; for SQL and / for PL/SQL blocks',
      severity: 'minor',
    },
    {
      feature: 'windows-auth',
      description: 'Oracle external authentication (Kerberos/LDAP/OCI IAM) is not supported in v1',
      severity: 'minor',
    },
    {
      feature: 'multiple-result-sets',
      description: 'Oracle does not return multiple result sets from a single statement; requires REF CURSOR for multi-result patterns',
      severity: 'minor',
    },
    {
      feature: 'streaming-results',
      description: 'Streaming results not implemented for Oracle',
      severity: 'minor',
    },
    {
      feature: 'batched-result-fetching',
      description: 'Batched result fetching not implemented for Oracle',
      severity: 'minor',
    },
    {
      feature: 'prepared-statements',
      description: 'Prepared statements not implemented for Oracle',
      severity: 'minor',
    },
  ],
};

/**
 * Kusto (Azure Data Explorer / KQL) engine definition.
 * Append-only analytics engine: no transactions, no schemas, no editing.
 */
export const KUSTO_ENGINE: EngineDefinition = {
  id: 'kusto',
  display: {
    displayName: 'Azure Data Explorer',
    shortName: 'KQL',
    icon: 'kusto',
    defaultPort: 0,
    connectionStringExample: 'https://mycluster.westeurope.kusto.windows.net',
  },
  connectionPicker: {
    label: 'Azure Data Explorer', group: 'azure', blurb: 'Cluster URL and Entra sign-in',
    summary: 'Kusto clusters in Azure Data Explorer', keywords: 'adx kusto kql', order: 6,
  },
  capabilities: {
    queryExecution: {
      supportsMultipleResultSets: true,
      supportsServerSideCancellation: true,
      supportsBatchedResultFetching: false,
      supportsStreamingResults: false,
      supportsCursors: false,
      supportsBatchSeparatorGo: false,
      supportsTransactions: false,
      supportsPreparedStatements: false,
    },
    languageServices: {
      hasLanguageServer: true,
      languageServerType: 'monaco-kusto',
      supportsCompletions: true,
      supportsHover: true,
      supportsSignatureHelp: true,
      supportsDiagnostics: true,
      supportsFormatting: false,
    },
    scripting: {
      supportsCreateTable: false,
      supportsAlterTable: false,
      supportsCreateIndex: false,
      supportsCreateView: false,
      supportsCreateProcedure: false,
      supportsCreateFunction: false,
    },
    connection: {
      supportsWindowsAuth: false,
      supportsSSL: false, // always HTTPS; flag off hides the checkbox
      supportsConnectionPooling: false,
      supportsDatabaseSelection: true,
      supportsSchemas: false,
    },
    tableExplorer: {
      supportsQueryPreview: true,
      supportsInboundForeignKeyExplorer: false,
      supportsEditing: false,
    },
    security: {
      supportsSecurityManagement: false,
      supportsServerLevelPrincipals: false,
      supportsDatabaseUsers: false,
      supportsDeny: false,
    },
    intellisense: {
      // Kusto uses the monaco-kusto language server (see languageServerType),
      // not the SQL IntelliSense resolver pipeline — all SQL-resolver flags off.
      structuralParser: 'none',
      semanticParser: 'none',
      supportsFkDrivenJoin: false,
      supportsAliasResolution: false,
      supportsCteScope: false,
      supportsPackageProc: false,
      keywordCasingConfigurable: false,
    },
    workspaceFeatures: {
      // Notebooks: stateless per-cell dispatch (notebookKustoSession.ts) — no
      // pinned session state; kusto is stateless HTTP (.show-only policy applies per cell).
      supportsNotebooks: true,
      // Schema Overview: electron schemaOverviewService has no kusto catalog queries.
      supportsSchemaOverview: false,
      supportsQueryEditor: true,
      supportsTableExplorer: true,
    },
    spatial: {
      // KQL has geo_* functions but no persisted geometry column type in our model.
      supportsSpatialTypes: false,
      supportsSpatialFilters: false,
    },
  },
  dangerousQueryPatterns: {
    alwaysDangerous: [],
    dangerousWithoutWhere: [],
    // Any control command other than .show is blocked by the adapter (S2);
    // this pattern keeps editor-side messaging consistent. The risk gate
    // evaluates kusto through a KQL-specific branch (never the SQL splitter).
    engineSpecific: [
      { id: 'kusto.management-command', pattern: /^\s*\.(?!show\b)\w[\w-]*/im, reasonClass: 'unsupported-management' },
    ],
    getConfirmationMessage: () =>
      'Management commands (.create, .drop, .ingest, …) are not supported yet for Azure Data Explorer connections. Only queries and .show commands run.',
  },
  connectionErrorPatterns: {
    connectionLost: [/ENOTFOUND/i, /ECONNREFUSED/i, /ETIMEDOUT/i, /ECONNRESET/i, /getaddrinfo/i, /Failed to get cloud info/i],
  },
  parityGaps: [
    {
      feature: 'streaming-results',
      description: 'Kusto responses arrive as a whole; no row streaming.',
      severity: 'minor',
    },
    {
      feature: 'prepared-statements',
      description: 'Not applicable to KQL.',
      severity: 'minor',
    },
  ],
};

/**
 * Azure Storage engine definition.
 * Blob / Table / Queue / File / ADLS Gen2 browser: no query language, no Query
 * Editor, no Table Explorer tab (entities render inside the storage-explorer tab).
 * Every SQL-side registry refuses this engine (`azureStorageRefusal.ts`).
 */
export const AZURE_STORAGE_ENGINE: EngineDefinition = {
  id: 'azurestorage',
  display: {
    displayName: 'Azure Storage',
    shortName: 'Storage',
    icon: 'azurestorage',
    defaultPort: 0,
    connectionStringExample: 'DefaultEndpointsProtocol=https;AccountName=contoso;AccountKey=...;EndpointSuffix=core.windows.net',
  },
  connectionPicker: {
    label: 'Azure Storage', group: 'azure', blurb: 'Account, SAS URL or local emulator',
    summary: 'Blob, Table, Queue and File shares', keywords: 'blob table queue files adls azurite sas', order: 7,
  },
  capabilities: {
    queryExecution: {
      supportsMultipleResultSets: false,
      // Listings / transfers carry an AbortSignal end-to-end (cancel IPC).
      supportsServerSideCancellation: true,
      supportsBatchedResultFetching: false,
      supportsStreamingResults: false,
      supportsCursors: false,
      supportsBatchSeparatorGo: false,
      supportsTransactions: false,
      supportsPreparedStatements: false,
    },
    languageServices: {
      hasLanguageServer: false,
      languageServerType: 'none',
      supportsCompletions: false,
      supportsHover: false,
      supportsSignatureHelp: false,
      supportsDiagnostics: false,
      supportsFormatting: false,
    },
    scripting: {
      supportsCreateTable: false,
      supportsAlterTable: false,
      supportsCreateIndex: false,
      supportsCreateView: false,
      supportsCreateProcedure: false,
      supportsCreateFunction: false,
    },
    connection: {
      supportsWindowsAuth: false,
      supportsSSL: false, // HTTPS by endpoint (http only for the loopback emulator); no checkbox
      supportsConnectionPooling: false,
      supportsDatabaseSelection: false, // no database notion (ruling F1: never `database = service`)
      supportsSchemas: false,
    },
    tableExplorer: {
      // The table-explorer TAB never opens for this engine; entity editing lives
      // in storage-explorer through the TableExplorerGrid host seam (S4).
      supportsQueryPreview: false,
      supportsInboundForeignKeyExplorer: false,
      supportsEditing: false,
    },
    security: {
      supportsSecurityManagement: false,
      supportsServerLevelPrincipals: false,
      supportsDatabaseUsers: false,
      supportsDeny: false,
    },
    intellisense: {
      structuralParser: 'none',
      semanticParser: 'none',
      supportsFkDrivenJoin: false,
      supportsAliasResolution: false,
      supportsCteScope: false,
      supportsPackageProc: false,
      keywordCasingConfigurable: false,
    },
    workspaceFeatures: {
      supportsNotebooks: false,
      supportsSchemaOverview: false,
      supportsQueryEditor: false,
      supportsTableExplorer: false,
    },
    spatial: {
      supportsSpatialTypes: false,
      supportsSpatialFilters: false,
    },
  },
  dangerousQueryPatterns: {
    alwaysDangerous: [],
    dangerousWithoutWhere: [],
    engineSpecific: [],
    getConfirmationMessage: () => 'Azure Storage connections have no query language.',
  },
  connectionErrorPatterns: {
    // Same list as KUSTO_ENGINE (both are HTTPS SDKs over Node sockets).
    connectionLost: [/ENOTFOUND/i, /ECONNREFUSED/i, /ETIMEDOUT/i, /ECONNRESET/i, /getaddrinfo/i, /Failed to get cloud info/i],
  },
  parityGaps: [
    {
      feature: 'streaming-results',
      description: 'Not applicable: listings arrive as continuation-token pages, not streamed rows.',
      severity: 'minor',
    },
    {
      feature: 'prepared-statements',
      description: 'Not applicable: Azure Storage has no query language.',
      severity: 'minor',
    },
    {
      feature: 'multiple-result-sets',
      description: 'Not applicable: Azure Storage has no query language.',
      severity: 'minor',
    },
  ],
};

/**
 * Mock engine definition (for E2E testing).
 */
export const MOCK_ENGINE: EngineDefinition = {
  id: 'mock',
  display: {
    displayName: 'Mock Database',
    shortName: 'Mock',
    icon: 'mock',
    defaultPort: 0,
    connectionStringExample: 'mock://test',
  },
  capabilities: {
    queryExecution: {
      supportsMultipleResultSets: true,
      supportsServerSideCancellation: true,
      supportsBatchedResultFetching: true,
      supportsStreamingResults: false,
      supportsCursors: false,
      supportsBatchSeparatorGo: false,
      // Mock transaction session is wired (transactionEngineSession's
      // createMockSession); the mock transaction e2e specs exercise the
      // Query Editor transaction toolbar through it.
      supportsTransactions: true,
      supportsPreparedStatements: false,
    },
    languageServices: {
      hasLanguageServer: true,
      languageServerType: 'mock',
      supportsCompletions: true,
      supportsHover: true,
      supportsSignatureHelp: false,
      supportsDiagnostics: true,
      supportsFormatting: true,
    },
    scripting: {
      supportsCreateTable: true,
      supportsAlterTable: true,
      supportsCreateIndex: true,
      supportsCreateView: true,
      supportsCreateProcedure: true,
      supportsCreateFunction: true,
    },
    connection: {
      supportsWindowsAuth: false,
      supportsSSL: false,
      supportsConnectionPooling: false,
      supportsDatabaseSelection: true,
      supportsSchemas: true,
    },
    tableExplorer: {
      supportsQueryPreview: true,
      supportsInboundForeignKeyExplorer: true,
      supportsEditing: true,
    },
    security: {
      supportsSecurityManagement: false, // Mock engine doesn't support security management
      supportsServerLevelPrincipals: false,
      supportsDatabaseUsers: false,
      supportsDeny: false,
    },
    intellisense: {
      structuralParser: 'none',
      semanticParser: 'none',
      supportsFkDrivenJoin: false,
      supportsAliasResolution: false,
      supportsCteScope: false,
      supportsPackageProc: false,
      keywordCasingConfigurable: false,
    },
    workspaceFeatures: {
      // Both are special-cased in the electron IPC handlers ahead of any real
      // pool routing (registerSchemaOverviewIpcHandlers' isMockConnection branch;
      // notebookSessionApi's NotebookEngineType union includes 'mock').
      supportsNotebooks: true,
      supportsSchemaOverview: true,
      supportsQueryEditor: true,
      supportsTableExplorer: true,
    },
    spatial: {
      // {true,false} so mock-engine e2e can exercise the spatial view surfaces.
      supportsSpatialTypes: true,
      supportsSpatialFilters: false,
    },
  },
  dangerousQueryPatterns: {
    // NOTE (WS-E): the risk gate (`src/features/sql-safety/riskRules.ts`)
    // ALIASES mock → the full MSSQL pattern table for e2e realism (CK-E §2),
    // so this table is not consulted by the gate. It stays populated so the
    // EngineDefinition shape remains uniform.
    alwaysDangerous: [
      { id: 'mock.drop-structure', pattern: /\bDROP\s+(TABLE|DATABASE|SCHEMA|INDEX)\b/i, severity: 'high', reasonClass: 'destructive' },
      { id: 'mock.truncate-table', pattern: /\bTRUNCATE\s+TABLE\b/i, severity: 'high', reasonClass: 'destructive' },
      { id: 'mock.alter-table-drop', pattern: /\bALTER\s+TABLE\s+.*\bDROP\b/i, severity: 'high', reasonClass: 'destructive' },
      { id: 'mock.drop-routine', pattern: /\bDROP\s+(VIEW|PROCEDURE|FUNCTION|TRIGGER)\b/i, severity: 'low', reasonClass: 'routine-drop' },
    ],
    dangerousWithoutWhere: [
      { id: 'mock.delete-no-where', pattern: /\bDELETE\s+FROM\b/i },
      { id: 'mock.update-no-where', pattern: /\bUPDATE\s+\w+\s+SET\b/i },
    ],
    engineSpecific: [],
  },
  connectionErrorPatterns: {
    connectionLost: [
      // Mock engine uses generic patterns for testing
      /connection.*lost/i,
      /not connected/i,
      /ECONNREFUSED/i,
    ],
  },
  parityGaps: [], // Mock engine is for testing only
};

/**
 * Central registry of all engine definitions.
 */
export const ENGINE_REGISTRY: Readonly<Record<DatabaseEngineId, EngineDefinition>> = {
  mssql: MSSQL_ENGINE,
  pgsql: PGSQL_ENGINE,
  sqlite: SQLITE_ENGINE,
  mysql: MYSQL_ENGINE,
  oracle: ORACLE_ENGINE,
  kusto: KUSTO_ENGINE,
  azurestorage: AZURE_STORAGE_ENGINE,
  mock: MOCK_ENGINE,
};

/**
 * Default ports for each engine (convenience export).
 */
export const DEFAULT_PORTS: Readonly<Record<DatabaseEngineId, number>> = {
  mssql: 1433,
  pgsql: 5432,
  sqlite: 0,
  mysql: 3306,
  oracle: 1521,
  kusto: 0,
  azurestorage: 0,
  mock: 0,
};
